import { Injectable, inject } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest } from '@angular/common/http';
import { Observable, from } from 'rxjs';
import { SessionKeys } from '../enums/session-keys.enums';
import * as Cookies from 'es-cookie';
import { UserSessionService } from '../services';
import * as JWT from '../functions/jwt-helper.functions';
import { switchMap, take, filter } from 'rxjs/operators';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
	private _userSessionService = inject(UserSessionService);

	private _refreshInProgress: boolean = false;

	intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
		if (!request.url.includes('/api')) {
			return next.handle(request);
		}

		if (this._refreshInProgress) {
			console.log(`Token Refresh In Progress`);

			return this._userSessionService.tokenSubject.pipe(
				filter((token) => token !== null),
				take(1),
				switchMap(() => {
					console.log('Token Received From tokenSubject.');
					return next.handle(this.addAuthToken(request));
				})
			);
		} else {
			const token: string = this.getToken();

			if (this.needsRefresh(token)) {
				console.log(`%cToken Is Expired. Refresh It.`, 'color: blue; font-weight: bold;');

				this._refreshInProgress = true;

				return from(
					this._userSessionService
						.refreshToken()
						.then(() => {
							console.log('Token Refreshed.');
						})
						// finally, not then: refreshToken can reject (silent sign-in failed), and the flag
						// must clear either way or every later request would park on tokenSubject forever.
						// A rejection also errors this request's stream, which is intended: the session is
						// gone and cross-tab token watchers send the user back to sign-in.
						.finally(() => {
							this._refreshInProgress = false;
						})
				).pipe(
					switchMap(() => {
						console.log(`%cContinue Request`, 'color: red; font-weight: bold;');

						return next.handle(this.addAuthToken(request));
					})
				);
			} else {
				return next.handle(this.addAuthToken(request));
			}
		}
	}

	private getToken(): string {
		return Cookies.get(SessionKeys.SESSION_USER_TOKEN);
	}

	/**
	 * isTokenExpired throws on a missing or malformed token, and it is called on every /api request.
	 * Once the session cookie is gone that throw escapes intercept() and turns each request into a
	 * global error, which is how one dead session becomes an error storm. Treat unreadable as expired.
	 */
	private needsRefresh(token: string): boolean {
		if (!token) {
			return true;
		}

		try {
			return JWT.isTokenExpired(token);
		} catch {
			return true;
		}
	}

	private addAuthToken(request: HttpRequest<unknown>) {
		const authRequest: HttpRequest<unknown> = request.clone({
			headers: request.headers.set('Authorization', `Bearer ${this.getToken()}`)
		});

		return authRequest;
	}
}
